// Single source of truth for ABDM wording (M1 ABHA, M2 link records, M3 records from other
// organisations) - owner-approved glossary R169/R171/R172. Components import from here so the
// wording can't drift. NHA-verbatim texts (OTP hints, consent statements, NHA error texts)
// are NOT here - they stay byte-identical where they live. Record-type names: HI_TYPE_LABELS.

// R173: one icon per module (no NHA/ABDM logos).
export const ABDM_ICONS = {
  ABHA: '🪪',
  LINK_RECORDS: '🔗',
  OTHER_ORGS: '📥',
};

// ── Terms ─────────────────────────────────────────────────────────────
export const ABHA_TITLE = 'ABHA';
// R183: module HEADINGS (dashboard cards + page titles) keep the module tag; nowhere else.
export const MODULE_TITLES = {
  ABHA: 'ABHA (M1)',
  LINK_RECORDS: 'Link health records to ABHA (M2)',
  OTHER_ORGS: 'ABHA consent & records (M3)',
};
export const VERIFY_ABHA = 'Verify ABHA';
export const CREATE_ABHA = 'Create ABHA';
export const ADD_ABHA = 'Add ABHA';
export const SCAN_SHARE = 'Scan & Share';
export const SKIP_ABHA = 'Skip ABHA for now';
export const ABHA_NUMBER_LABEL = 'ABHA number:';
export const ABHA_ADDRESS_LABEL = 'ABHA address:';
export const NO_MORE_RESENDS = 'No more OTP resends. Go back and start again.';
export const REGISTER_OPD = 'Register OPD visit';
export const ADMIT_IPD = 'Admit patient (IPD)';

// ── M1: dashboard + ABHA page ─────────────────────────────────────────
export const ABHA_CARD = { title: MODULE_TITLES.ABHA, subtitle: `Create · Verify · ${SCAN_SHARE}` };
export const ABHA_PAGE = {
  subtitle: `Ayushman Bharat Health Account (ABHA) · Create · Verify · ${SCAN_SHARE}`,
  tabCard: 'Create / Verify',
  tabFind: "Find patient's ABHA",
  findHint: 'Find a registered patient by mobile number to see whether their ABHA is on file.',
  findError: "Could not check this patient's ABHA. Please try again.",
  noAbhaOnFile: 'No ABHA on file',
};

// ── M1: verify flow ───────────────────────────────────────────────────
export const VERIFY_FLOW = {
  title: VERIFY_ABHA,
  subtitle: "Find the patient's ABHA with their mobile number, Aadhaar, ABHA number or ABHA address.",
  mobileTab: 'Mobile number',
  abhaNumberHint: 'ABHA number must be 14 digits.',
  sendOtpTo: 'Send OTP to',
  optAbhaMobile: 'ABHA linked mobile number',
  optAadhaarMobile: 'Aadhaar linked mobile number',
  statusActive: 'Active',
  statusInactive: 'Inactive',
  // Fallbacks when ABDM sends no mask (same pattern as the NHA line, minus "ending with ...").
  otpSentAbhaMobile: 'OTP sent to ABHA linked mobile number',
  otpSentAadhaarMobile: 'OTP sent to Aadhaar linked mobile number',
};

// ── M1: create flow ───────────────────────────────────────────────────
export const CREATE_FLOW = {
  stepLabels: ['Consent', 'OTP', 'ABHA address', 'ABHA card'],
  title: CREATE_ABHA,
  subtitle: "Using the patient's Aadhaar OTP",
  aadhaarHint: "Enter the patient's 12-digit Aadhaar number",
  mobileLabel: "Patient's mobile number",
  mobileHint: 'The Aadhaar OTP goes to the Aadhaar-linked mobile. If this number is different, we verify it with one more OTP.',
  otpFallback: 'We just sent an OTP on the Mobile Number linked with Aadhaar. Enter the OTP below to proceed with ABHA creation',
  mobileStepTitle: "Verify the patient's mobile number.",
  chooseAddress: 'Choose an ABHA address',
  otherAddress: 'Type a different ABHA address',
  savingAddress: 'Saving ABHA address…',
  confirmAddress: 'Confirm ABHA address',
  foundExisting: 'ABHA already exists',
  created: 'ABHA created',
  existsConfirm: 'This ABHA already exists. Confirm with an OTP to download the ABHA card.',
  verifyToDownload: 'Verify to download the ABHA card',
  verifyToDownloadSub: 'Confirm with the same Aadhaar number to download the ABHA card',
  continueToRegistration: `${REGISTER_OPD} →`,
  // Existing ABHA: add another ABHA address (owner 2026-11-01). Wording owner-approved 2026-11-02.
  // ABDM's enrol/abha-address only accepts preferred: 1, hence the warning + tick.
  addressChoiceTitle: 'Which ABHA address should we use?',
  preferredTag: '(preferred)',
  newAddressOption: 'Create a new ABHA address',
  newAddressGo: 'Continue →',
  addAddressWarning: "This new ABHA address will become the patient's preferred ABHA address, in their ABHA app and with every other provider. Their current ABHA address keeps working.",
  addAddressConfirm: 'I have told the patient, and they agree to make this new address their preferred ABHA address',
  addAddressBack: '← Back',
};

// ── M1: link to patient + ABHA section + scan & share ─────────────────
export const LINK_CONFIRM = {
  note: "Only visits, lab reports and admissions from today onwards can be linked to the patient's ABHA. Records from before today are never linked.",
  cardTitle: 'ABHA profile',
};
export const ABHA_SECTION = {
  header: ABHA_TITLE,
  onFile: 'ABHA on file',
  degraded: `⚠ ABHA details could not be loaded. ${VERIFY_ABHA} to show them again.`,
  sourceScanShare: `Added by ${SCAN_SHARE}`,
  sourceVerify: `Added by ${VERIFY_ABHA}`,
  sourceFile: 'Already on file',
  sourceCreate: `Added by ${CREATE_ABHA}`,
  verifyButton: VERIFY_ABHA,
};
export const ADD_ABHA_MODAL_TITLE = ADD_ABHA;
export const SCAN_SHARE_COPY = {
  token: (n) => `Token ${n}`,
  settingsLink: `Go to Settings → ${SCAN_SHARE} counters`,
  claimFailed: (detail) => `Could not add the ABHA from ${SCAN_SHARE}: ${detail}`,
  verifyInstead: "Verify ABHA from the patient's record instead.",
};
export const ABHA_SERVICE_ERROR = 'ABHA service is not responding. Please try again, or skip ABHA for now.';

// M1 captcha before a verify-by-mobile OTP first send (NHA VRFY_ABHA_301 -- the only place the sheet asks). Wording owner-approved 2026-11-02.
// Server texts for CAPTCHA_REQUIRED / CAPTCHA_INVALID / CAPTCHA_UNAVAILABLE are shown as sent.
export const CAPTCHA_COPY = {
  label: 'Type the code shown in the picture',
  imageAlt: 'Security code picture. Type the letters and numbers in it.',
  refresh: 'Show another code',
  loading: 'Loading code…',
  loadFailed: 'Could not load the code. Check the internet connection and try again.',
  retry: 'Try again',
  startAgain: 'That code did not work. Type the new code shown, then send the OTP.',
};

// ── M2: link health records to ABHA ───────────────────────────────────
export const LINKED_RECORDS = {
  title: 'Health records linked to ABHA',
  empty: 'No health records linked to ABHA yet.',
  loadError: 'Could not load health records linked to ABHA.',
  loading: 'Loading…',
};
export const LINK_STATUS = {
  LINKED: 'Linked',
  NOTIFIED: 'Linking…',
  CREATED: 'Not linked',
  LINK_PENDING: 'Linking…',
  FAILED: 'Link failed',
  ARCHIVED: 'Archived',
};
export const RETRY_LINK = 'Retry link';
export const LINK_COPY = {
  retrying: 'Retrying…',
  retryError: 'Could not link. Please try again.',
  retryOk: 'Trying to link again…',
  bundleWarning: "Linked, but the record could not be prepared for the patient's ABHA app. Click Retry link.",
  noSummary: 'Write the discharge summary first, then link it to ABHA.',
  notVerified: 'The patient’s ABHA is not verified, so the summary cannot be linked.',
  beforeAbha: 'This admission was recorded before the patient’s ABHA was linked, so it cannot be linked.',
  dischargeFallback: 'Could not link the discharge summary. Please try again.',
  confirmCta: 'Link to ABHA',
  modeNote: 'Linking is not available because this organisation is not set up as a Health Facility on ABDM.',
  panelLoading: 'Loading…',
  panelError: 'Could not load the Health Facility status.',
  panelTryAgain: 'Try again',
  panelChecking: 'Checking…',
};
export const SETTINGS_COPY = {
  saveError: 'Could not save. Please try again.',
  hipTitle: `${ABDM_ICONS.ABHA} Health Facility ID on ABDM (HIP ID)`,
  hipText: "Your organisation's Health Facility ID (HIP ID) from the ABDM Health Facility Registry. Needed to link health records to patients' ABHA and for Scan & Share counters.",
  hiuTitle: `${ABDM_ICONS.OTHER_ORGS} ID for records from other organisations (HIU ID)`,
  hiuText: "Your organisation's HIU ID on ABDM. Needed to ask patients for consent and get their records from other organisations. Different from the Health Facility ID above.",
  hipNameText: "Your organisation's name as registered on ABDM. It fills in from ABDM when you save the HIP ID, and is shown on every record linked to ABHA. If blank, your organisation name is used.",
  hipNameLabel: 'Name registered with ABDM',
  fromAbdm: 'From ABDM',
  nameFromAbdmHint: 'This name comes from ABDM, so it cannot be changed here.',
  statusConnected: 'Connected',
  // WORDING-PENDING (owner to approve)
  fixInSettings: 'Fix in Settings',
  // Owner-approved (R233)
  hipAbdmOff: 'ABDM is switched off for this organisation. You can save the HIP ID now. Once ABDM is switched on, press "Check with ABDM again" to verify it.',
  statusVerified: 'Verified with ABDM',
  statusPending: 'Linkage pending with client ID',
  // One plain reason line per backend reason code (owner-approved R228).
  statusReasons: {
    OTHER_BRIDGE: 'This HIP ID is registered under a different ABDM client ID.',
    NOT_REGISTERED: "ABDM doesn't recognise this HIP ID yet.",
    NOT_HIP: "ABDM doesn't list this ID as a Health Information Provider.",
    INACTIVE: 'This HIP ID is not active on ABDM.',
    UNREACHABLE: "ABDM couldn't be reached. We'll check again next time you save.",
    NOT_CONFIGURED: 'ABDM is not set up for this organisation yet.',
  },
  // Owner-approved (R228).
  recheckLabel: 'Check with ABDM again',
  rechecking: 'Checking…',
  stillPending: 'Still pending with ABDM.',
  recheckError: 'Could not check with ABDM. Please try again.',
  saveLabel: 'Save',
  savingLabel: 'Saving…',
  savedLabel: 'Saved ✓',
  saveDisabledHint: 'Change the HIP ID or name to save.',
  noHipBold: 'Add the Health Facility ID (HIP ID) first.',
  noHipRest: 'Counter QR codes need your organisation’s HIP ID. Enter it in "Health Facility ID on ABDM" above, then come back here.',
};

// ── M3: records from other organisations ──────────────────────────────
export const OTHER_ORGS_TITLE = MODULE_TITLES.OTHER_ORGS;
export const OTHER_ORGS = {
  cardSubtitle: 'Ask consent · View records',
  listSubtitle: 'Ask patients for consent and get their ABHA records from other organisations.',
  newRequest: '+ New consent request',
  emptyList: 'No consent requests yet',
  notSent: 'Not sent',
  requestTitle: 'New consent request',
  requestBack: 'All consent requests',
  requestSubtitle: 'Ask the patient for consent to see their health records from other organisations. They approve it in their ABHA app.',
  findTitle: VERIFY_ABHA,
  findSubtitle: "Verify the patient's ABHA first. The consent request goes to their ABHA app.",
  recordTypes: 'Record types',
  recordTypesHint: 'Keep all types selected.',
  pickType: 'Choose at least one record type.',
  pickTypeAndDates: 'Choose at least one record type and a date range.',
  accessUntil: 'Access until',
  // FT-R6: how long the patient has to answer before WE mark the request Expired.
  requestExpiresIn: 'Request expires in',
  requestExpiresHelp: "If the patient doesn't answer by then, the request expires and you will need to send a new one.",
  // minutes = the value the form sends as request_expires_in_minutes (single source with the label).
  expiryPresets: [
    { minutes: 2, label: '2 minutes' },
    { minutes: 15, label: '15 minutes' },
    { minutes: 60, label: '1 hour' },
    { minutes: 360, label: '6 hours' },
    { minutes: 1440, label: '24 hours' },
  ],
  expiryCustom: 'Custom',
  expiryCustomAmount: 'Custom expiry, number',
  expiryUnits: { minutes: 'minutes', hours: 'hours' },
  expiryCustomError: 'Choose a time between 2 minutes and 24 hours.',
  // Detail line / list-row prefix for a REQUESTED consent: when the request expires.
  requestExpires: 'Request expires',
  purposeMissing: 'Choose a purpose.',
  verifyFirst: "Verify the patient's ABHA first.",
  sendError: 'Could not send the consent request. Please try again.',
  send: 'Send consent request',
  sending: 'Sending…',
  selfRequested: 'Self Requested',
  getRecords: 'Get records',
  gettingRecords: 'Getting records…',
  getFailed: 'Could not get the records. You can try again.',
  organisation: 'Organisation',
  fromOrgs: (n, names) => `Records will come from ${n} organisations: ${names}.`,
  scopeTypes: 'Record types',
  rawForbidden: "You don't have permission to see the raw data.",
  rowRequested: 'Requested',
  rowRecords: 'Records',
  rowAccessUntil: 'Access until',
  // FT-R7: what the request asked for, on the list row and the detail page.
  purpose: 'Purpose',
  abhaNumber: 'ABHA number',
  moreTypes: (n) => `+${n}`,
  // Records-from-other-organisations detail + list (R186 mockup)
  viewRecords: 'View records →',
  viewTheseRecords: 'View these records →',
  viewNRecords: (n) => `View ${n} record${n === 1 ? '' : 's'} →`,
  recordsReceivedRow: (n) => `✓ ${n} record${n === 1 ? '' : 's'} received`,
  noRecordsYet: 'No records received yet',
  stepNames: ['Not requested', 'Requested', 'Receiving', 'Received'],
  stepFailed: 'Failed',
  thisOrganisation: 'This organisation',
  ownOrgText: 'These records are from this organisation. Check the patient card to see them.',
  ownNotIncluded: 'Your own organisation is not included.',
  waitingFor: (label) => `Waiting for ${label} to send records.`,
  waitingUsual: 'This usually takes a few minutes. You can leave this page; records appear here when they arrive.',
  receivedSoFar: (n) => `${n} record${n === 1 ? '' : 's'} received so far. More may still be coming.`,
  readArrived: 'You can read the ones that arrived below.',
  askedAt: 'Requested at',
  lastChecked: 'Last checked',
  checksAuto: 'Checks again automatically',
  checkNow: 'Check now',
  checking: 'Checking…',
  tryAgain: 'Try again',
  askAllHint: 'Asks every organisation that has not sent its records yet.',
  longerBold: 'This is taking longer than usual.',
  longerRest: (label) => `${label} has not sent the records yet. You can keep waiting, or press Try again.`,
  failedBold: (label) => `${label} did not send the records.`,
  failedRest: 'Their system may be busy, or the request took too long. Try again in a few minutes.',
  receivedCount: (n) => `${n} record${n === 1 ? '' : 's'} received.`,
  partialNote: (label) => `Some records from ${label} were not sent or could not be read.`,
  notRequestedHint: 'Records have not been requested from this organisation yet. Use Get records above.',
  // "1 of N records received" when the organisation named N records (P3.2).
  receivedOf: (n, named) => `${n} of ${named} record${named === 1 ? '' : 's'} received.`,
  // Not-sent reason on a SUBMIT_FAILED ("Not sent") consent whose reason code is ABDM_REJECTED.
  // list: stands alone on the list row. card: sits inside the detail card, which adds the next step.
  notSentReasons: {
    ABDM_REJECTED: {
      list: 'ABDM did not accept this request.',
      card: 'ABDM did not accept it.',
    },
  },
  // Old consent without stored refs: the server refuses Get records until Check now has run.
  scopeUnknownHint: 'Press Check now first, then Get records.',
};

// Per-organisation failure lines keyed by the server's failure_code (P3.2), as { bold, rest }.
// Both take the organisation's name. Swapping the approved wording touches ONLY this table.
export const FAILURE_LINES = {
  TIMEOUT: {
    bold: (l) => `${l} did not send the records in time.`,
    rest: () => 'We waited 2 hours. Try again in a few minutes.',
  },
  ABDM_REFUSED: {
    bold: (l) => `ABDM did not accept the request to ${l}.`,
    rest: () => 'Try again in a few minutes. If it fails again, tell your admin.',
  },
  NOTHING_SENT: {
    bold: (l) => `${l} replied but sent no records.`,
    rest: () => 'They may have none for the dates and record types the patient allowed.',
  },
  CONSENT_ENDED: {
    bold: (l) => `The patient's consent has ended, so ${l}'s records were not accepted.`,
    rest: () => 'To get them, send a new consent request.',
  },
  PUSH_WINDOW_CLOSED: {
    bold: (l) => `${l}'s records came too late and were not accepted.`,
    rest: () => 'Try again to ask for them once more.',
  },
  DECRYPT_FAILED: {
    bold: (l) => `${l}'s records arrived but could not be read.`,
    rest: () => 'Try again. If it fails again, tell your admin.',
  },
};
// fail.UNKNOWN: no code, an unmapped code, or a legacy failure_reason only.
export const UNKNOWN_FAILURE_LINE = {
  bold: (l) => OTHER_ORGS.failedBold(l),
  rest: () => OTHER_ORGS.failedRest,
};

// Generic record viewer fallback note (G5).
export const GENERIC_RECORD_NOTE = "This type of record can't be shown in a neat layout yet. Its details are below.";

// Status cards on the consent detail page (R191 mockup screen 6)
export const STATUS_CARDS = {
  REQUESTED: { kind: 'info', bold: 'Waiting for the patient to approve in their ABHA app.', rest: 'Ask the patient to open the notification. Nothing more to do here until they approve.' },
  DENIED: { kind: 'err', bold: 'The patient said no.', rest: 'You can no longer get or view records for this request. If the patient changes their mind, send a new consent request.', action: true },
  EXPIRED: { kind: 'gray', bold: 'Access has ended.', rest: 'Records received earlier are no longer available here. To see records again, send a new consent request.', action: true },
  REVOKED: { kind: 'err', bold: 'The patient withdrew their consent.', rest: 'You can no longer get or view records for it. Please do not ask again without speaking to the patient.', action: true },
  SUBMIT_FAILED: { kind: 'warn', bold: 'This request never reached the patient.', rest: "Check the patient's ABHA address, then send a new consent request.", reasonFallback: 'The request could not be sent to ABDM.', action: true },
};
export const STATUS_CARD_NEW_REQUEST = 'New consent request';
export const STATUS_CARD_NO_PERMISSION = "You don't have permission to request records. Ask your admin.";
export const ACCESS_ENDED = 'Access ended';

// Records view (R191 mockup screen 4)
export const RECORDS_VIEW = {
  title: 'Records received',
  back: 'Back to consent request',
  backToRecords: '← Back to records',
  fromOrgs: (n) => `From ${n} organisation${n === 1 ? '' : 's'}`,
  search: 'Search records',
  allTypes: 'All types',
  colRecord: 'Record',
  colDate: 'Date',
  colOrg: 'Organisation',
  view: 'View',
  showMore: (n) => `Show ${n} more`,
  groupCount: (n) => `${n} record${n === 1 ? '' : 's'}`,
  none: 'No records received yet.',
  noMatch: 'No records match.',
  loading: 'Loading records…',
  loadError: 'Could not load records.',
  orgLoadError: (label) => `Could not load the records from ${label}.`,
  inactive: 'Records can only be viewed while the consent is active.',
  medicines: (n) => `${n} medicine${n === 1 ? '' : 's'}`,
};

// "Bill" section for a received Invoice (ClinicalRecordRenderer). Column labels match the bill's own columns.
export const INVOICE_VIEW = {
  section: 'BILL',
  bill: 'Bill',
  cancelled: 'Cancelled',
  unnamedItem: 'Unnamed item',
  colItem: 'Item',
  colQty: 'Qty',
  colRate: 'Rate',
  colDiscount: 'Discount',
  colTax: 'Tax',
  colSurcharge: 'Surcharge',
  total: 'Total',
  totalInclTax: 'Total incl. tax',
};
